/** Lo que comparten las tres vistas de nómina. */

export type Frecuencia = "SEMANAL" | "QUINCENAL" | "MENSUAL"
export type TipoConcepto = "ASIGNACION" | "DEDUCCION"
export type FormaCalculo = "MONTO_FIJO" | "PORCENTAJE_SUELDO"
export type EstadoPeriodo = "BORRADOR" | "APROBADO" | "ANULADO"

/** Las tres que tiene sentido pagarle a alguien. Ver `nomina.FRECUENCIAS`. */
export const FRECUENCIAS: { valor: Frecuencia; etiqueta: string }[] = [
  { valor: "SEMANAL", etiqueta: "Semanal" },
  { valor: "QUINCENAL", etiqueta: "Quincenal" },
  { valor: "MENSUAL", etiqueta: "Mensual" },
]

/** ISO: el 1 es lunes, como `date.isoweekday()` en el servidor. */
export const DIAS_SEMANA = [
  { valor: 1, etiqueta: "Lunes" },
  { valor: 2, etiqueta: "Martes" },
  { valor: 3, etiqueta: "Miércoles" },
  { valor: 4, etiqueta: "Jueves" },
  { valor: 5, etiqueta: "Viernes" },
  { valor: 6, etiqueta: "Sábado" },
  { valor: 7, etiqueta: "Domingo" },
] as const

export type ConceptoAsignado = {
  id: number
  concepto_id: number
  concepto: string | null
  tipo: TipoConcepto | null
  forma_calculo: FormaCalculo | null
  valor: string | null
}

export type Contrato = {
  id: number
  persona_id: number
  persona: string | null
  proveedor_id: number
  proveedor: string | null
  sueldo_base: string
  divisa_id: number
  divisa: string | null
  frecuencia: Frecuencia
  dia_pago: number | null
  dia_semana: number | null
  vigente_desde: string
  vigente_hasta: string | null
  activo: boolean
  notas: string | null
  conceptos: ConceptoAsignado[]
}

export type Concepto = {
  id: number
  codigo: string
  nombre: string
  tipo: TipoConcepto
  forma_calculo: FormaCalculo
  valor: string
  descripcion: string | null
  activo: boolean
}

export type LineaRecibo = {
  id: number
  concepto_id: number
  descripcion: string
  tipo: TipoConcepto
  monto: string
}

export type Recibo = {
  id: number
  persona_id: number
  persona: string | null
  contrato_id: number
  proveedor_id: number
  sueldo_base: string
  divisa_id: number
  divisa: string | null
  total_asignaciones: string
  total_deducciones: string
  neto: string
  orden_id: number | null
  lineas: LineaRecibo[]
}

export type Periodo = {
  id: number
  codigo: string
  frecuencia: Frecuencia
  desde: string
  hasta: string
  fecha_pago: string
  estado: EstadoPeriodo
  notas: string | null
  creado_en: string
  aprobado_en: string | null
  recibos: Recibo[]
}

export type PeriodoResumen = Omit<Periodo, "notas" | "creado_en" | "aprobado_en" | "recibos">

/**
 * «Cada sábado», «El 15 y el último día»: la frase que hay que leer para saber cuándo
 * cobra, en vez de dos columnas con un número y un enum que hay que combinar.
 */
export function cuandoSePaga(c: {
  frecuencia: Frecuencia
  dia_pago: number | null
  dia_semana: number | null
}): string {
  if (c.frecuencia === "SEMANAL") {
    const dia = DIAS_SEMANA.find((d) => d.valor === c.dia_semana)?.etiqueta ?? "—"
    return `Cada ${dia.toLowerCase()}`
  }
  if (c.frecuencia === "QUINCENAL") return `El ${c.dia_pago} y el último día`
  return c.dia_pago === null ? "El último día del mes" : `El día ${c.dia_pago}`
}

export const TONO_ESTADO: Record<EstadoPeriodo, "neutro" | "exito" | "peligro"> = {
  BORRADOR: "neutro",
  APROBADO: "exito",
  ANULADO: "peligro",
}
